import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { IFormDelegate, IFormOptions, SECOND_ELEMENT_INDEX } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { IFliFormDialogData } from '../../../interfaces/fli-form-dialog-data.interface';
import { IFliFormDialogResult } from '../../../interfaces/fli-form-dialog-result.interface';

@UntilDestroy()
@Component({
    selector: 'templates-fli-form',
    templateUrl: './fli-form.dialog.html',
    styleUrls: ['./fli-form.dialog.scss'],
})
export class FliFormDialogComponent {
    formDelegate: IFormDelegate;

    options: IFormOptions;

    constructor(
        public dialogRef: MatDialogRef<FliFormDialogComponent, IFliFormDialogResult | undefined>,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private localizedTexts: I18NextService,
        @Inject(MAT_DIALOG_DATA) public data: IFliFormDialogData,
    ) {
        this.addSvgIcon('interface-delete 1');
        this.addSvgIcon('aumico-pattern-delete-filled-blue');
        this.applyLocalizations();
    }

    ngOnInit(): void {
        this.prepareFields();
        const that = this;
        this.formDelegate = {
            onSubmit(result: IFliFormDialogResult) {
                that.savePressed(result);
            },
        };
    }

    private savePressed(result: IFliFormDialogResult) {
        this.dialogRef.close({
            fliCaption: result.fliCaption,
            fliCashFlowType: result.fliCashFlowType,
            fliType: result.fliType,
            parentGroup: result.parentGroup,
        } as IFliFormDialogResult);
    }

    private applyLocalizations(): void {
        this.prepareFields();
    }

    private prepareFields(): void {
        const isUncoveredShortfallFli =
            this.data.fliToEdit != null ? this.data.fliToEdit.isUncoveredShortfallFli : false;
        this.options = {
            properties: [
                {
                    name: this.localizedTexts.t('aumico_templates_caption') as string,
                    type: 'text',
                    property: 'fliCaption',
                    required: true,
                },
                {
                    name: this.localizedTexts.t('aumico_templates_fli_form_fli_types') as string,
                    type: 'select',
                    multiple: false,
                    options: this.data.fliTypeOptions,
                    optionsLabel: this.localizedTexts.t('aumico_templates_fli_form_fli_type_options_label') as string,
                    property: 'fliType',
                    required: true,
                    disabled: isUncoveredShortfallFli,
                },
                {
                    name: this.localizedTexts.t('aumico_templates_fli_form_groups') as string,
                    type: 'select',
                    multiple: false,
                    options: this.data.groupOptions,
                    optionsLabel: this.localizedTexts.t('aumico_templates_fli_form_group_options_label') as string,
                    property: 'parentGroup',
                    required: true,
                    disabled: isUncoveredShortfallFli,
                },
            ],
            submitText: this.localizedTexts.t('aumico_templates_save') as string,
            invalidText: this.localizedTexts.t('aumico_templates_fli_form_error_name_already_exists') as string,
        };

        if (this.data.isCashFlowTypeForFliUsed) {
            this.options.properties.splice(SECOND_ELEMENT_INDEX, 0, {
                name: this.localizedTexts.t('aumico_templates_fli_form_cash_flow_types') as string,
                type: 'select',
                multiple: false,
                options: this.data.cashFlowTypeOptions,
                optionsLabel: this.localizedTexts.t('aumico_templates_fli_form_cash_flow_options_label') as string,
                property: 'fliCashFlowType',
                required: true,
            });
        }
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    closeDialog(): void {
        this.dialogRef.close();
    }
}
